import { NextPage } from "next"
import { useState, useRef, useCallback, useEffect, ChangeEvent } from "react"
import { makeId, getExtension, getFileSizeFromString } from '../util'
import type { IFile, IAllowFileExtensions, IMaxSize } from '../interfaces'
import FileList from '../listTheme/default'

interface IProps {
  className?: string;
  isMultiple?: boolean;
  allowExtensions?: IAllowFileExtensions;
  allowMaxSize?: IMaxSize;
  existingItems?: Record<string, any>[];
  handler?: (files: IFile[], existingItems: any) => void;
  downloadHandler?: (id: string) => void;
}

const Component: NextPage<IProps> = ({ className, isMultiple, allowExtensions, allowMaxSize, existingItems, handler, downloadHandler }) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [items, setItems] = useState<IFile[]>([]);
  const [localExistingItems, setLocalExistingItems] = useState<Record<string, any>[]>(existingItems || []);
  const maxSize = allowMaxSize || '5MB';
  const dragRef = useRef<HTMLDivElement | null>(null);
  const multiple = isMultiple || false;

  useEffect((): void => {
    handler && handler(items, localExistingItems);
  }, [handler, items, localExistingItems]);

  const isUploadable = useCallback((file: File): boolean => {
    if (items.filter((item: IFile) => { return item.object.name === file.name }).length > 0) {  // 이미 동일한 파일명이 업로드 되었는지 체크
      return false;
    } else if (allowExtensions && allowExtensions.includes(getExtension(file.name) as any) === false) {  // 허용된 확장자인지 체크
      return false;
    } else if (maxSize && file.size > getFileSizeFromString(maxSize)) {  // 허용된 용량인지 체크
      return false;
    } else {
      return true;
    }
  }, [allowExtensions, maxSize, items]);

  const onChangeFiles = useCallback((e: ChangeEvent<HTMLInputElement> | any): void => {
    const selectedFiles: File[] = Array.from((e.type === "drop") ? e.dataTransfer.files : e.target.files);
    if (selectedFiles.length <= 0) {
      return;
    }

    selectedFiles.map((file: File) => {
      if (isUploadable(file) === true) {
        setItems(currentItem => [...currentItem, ...[{ id: makeId(8), object: file }]]);
      }
    });
  }, [isUploadable]);

  const handleDeleteExistingFile = useCallback((id: string): void => {
    setLocalExistingItems(localExistingItems.filter((file: any) => file.id !== id));
  }, [localExistingItems]);

  const handleDeleteFile = useCallback((id: string): void => {
    setItems(items.filter((file: IFile) => file.id !== id));
  }, [items]);

  const handleDragEnter = useCallback((e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDragLeave = useCallback((e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDragOver = useCallback((e: DragEvent): void => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer!.files) { setIsDragging(true); } }, []);
  const handleDrop = useCallback((e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    onChangeFiles(e);
    setIsDragging(false);
  }, [onChangeFiles]);

  const initDragEvents = useCallback((): void => {
    if (!dragRef.current) return;
    dragRef.current.addEventListener("dragenter", handleDragEnter);
    dragRef.current.addEventListener("dragleave", handleDragLeave);
    dragRef.current.addEventListener("dragover", handleDragOver);
    dragRef.current.addEventListener("drop", handleDrop);
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  const resetDragEvents = useCallback((): void => {
    if (!dragRef.current) return;
    dragRef.current.removeEventListener("dragenter", handleDragEnter);
    dragRef.current.removeEventListener("dragleave", handleDragLeave);
    dragRef.current.removeEventListener("dragover", handleDragOver);
    dragRef.current.removeEventListener("drop", handleDrop);
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  useEffect(() => {
    initDragEvents();

    return () => resetDragEvents();
  }, [initDragEvents, resetDragEvents]);

  return (
    <div>
      {/* border-gray-300  */}
      <div ref={dragRef} className={`flex justify-center w-full px-6 py-6 border-2 border-dashed rounded-md bg-main-bg-secondary ${isDragging ? 'opacity-50 border-primary-800' : 'border-primary-300'}`}>
        <div className="space-y-1">
          <div className="flex flex-col items-center justify-center">
            <div className="my-6">
              <svg width="43" height="39" viewBox="0 0 43 39">
                <image width="43" height="39" xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACsAAAAnCAYAAACWn7G7AAAABHNCSVQICAgIfAhkiAAABU1JREFUWEfFmEtoXUUYx2eUohWDRWgp6cKFaGMeN0EoInZjKOjChV00+ERXgkgpKH1QNX1E7SOpxrQW1K3FRX10ZUWMrVbbWlvTNK2CKxEVq4tKYqBV9Pj75pyZO+fec8+Z+9B7w+GeTL6Z+eU7/+8xRw/sP6q0VkrpyFzmXsX3inudjMu9jGfbJvPlS0eP8fUA8w8zZ8LNkelmXXPT0EcPvPZpGbJ54GegGDP/oHx0tAO4za0C1gP7Pkt7tXHg9YDtNk8k9rAF3sX4plYA6/69x7xHXesxF0piI/LY6R5zNfAowBuaBdb9E8cqtFk38Kb4cft6T2nYengM4PXNAOv+Vz/PCKZg4CfZfH9A0FngYYBHGgXWpfEvUhFfjv5C4Kfw5r46skTTwAbWBURVuqoJvFbSUgNpzQJvZf62ej2sS68cj2VgI7gYeB2bjDeRhy3wdoC31AOsSy+fcKkrAHgdkOMtKBwWeATg4VBg3bfnRKoq5QCvBXKihZWubg3rvrGTVRUsA7gE5LRLT40XjrjcVufhQcaPFHlY941+mVnBKoBLBJOB/Y+AgY2OpDKL8Xu6l9C9u0/VrGAVwA8C/DCLLuSKkMQ/fF/LtZJ7/BXU/JzEdg7bq5jBT3SFPQ6x7htJL5E0OrbApIF1765TuRWsIOjYNPqT6+rAoFuK7cWybaYkagLr3p1fFVawHOAlbPyzwAa2l93YfRtQmjOBgT2daKOyWUkXhBrAnWz8A8AGNgAY7auZatswD+ueHadzGu5CYAPrZFAMXEKvM3X0EikP656XzohH7mOBJSx0wIg+dULIBe4kOAxsYJYgBeLZsBOHzcOPM0eC8l3d8+IZ1zQD/B2Dt7PxfCAwnlXIIIYNAJZ8jQziKC/wMEEbSeNyR7y2GtXdL3x9mcFrbLJmgdUsdCgxKEpriQyULBxy4pB8nWi2EPhOLI57J45Id49MSepZEG9mhP4IwAey82aVJDwZ+IfLmt2akUFgt3YvLIddYCt1RWBnGeiIH6EBHuL+YO28mQLpZK4ng0JgJ4MAYCnBk+YJiw91NKe7t5+dZaDDVhAGgVUH89OQA45loHwZ5AKbbFAtsUxJDGILrEtrc/q2bWdn8aLxbAI8BDCwsVGBh13qKqh0Vs9xng0rzYPYTnqHUIGdRgZRh4tMZWXgNJwHvIx5krqk7NrHlVcR+7E7F1iapbmZ9EozsFuBFc+WU8kQuyYyKARexoI/+imowMOSFqcCKp04yMB6tsBuORdrtpz7TIB5GnY9aIZHFmD7E9fiAGA0FS3CTvYLKc2xDMq2BpbqoK63osfDazB6x9OwzRK1NliB7ZtcN5tW0ebbOIJlDpAK70ccMqOPnaeKgU2AeU74Q3cNz8yz0HXWkyxsioKn4QSyUBKLRLvMEw/a04Bmbfn9Ul5FrKHhlczhDYzLPPO66/mZiwzS6rls8Ci/v5Uqh0aILg+HZAlXzQKzRFYQ38OeH3ryvATs+U8Au9v953TuGK2uqt//P/DrOPAJT57Tuuu58xsBcy/VEg8DHx1tI3Av+5uGx6t048BeWIxnf7VgicHfOHINE95vA/AqNPye5H7r1QT4Fr382Quilz388ekKYHnwHzCBl2Hqd/5OsCTR3lpJSADKJYVJ2kEkWFU53wb4IQMbB4GaAmggA9hVpDYF3ffA3wrXX3r55m9smlkI8EcAScrg453JEk+2QRJSmlfB8pvxtoG1MLGH5d2TSOKGNgJzIFB7AdzgS6IMmwa+EdD7Ab6L75v4xuvoyujAviXx+trmNCyFQyrf5aTS8fLNnFR+8Uu43P8LBniCwOESvvAAAAAASUVORK5CYII="/>
              </svg>
              {/* <svg
                className="w-12 h-12 mx-auto text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg> */}
            </div>
            <label
              htmlFor="fileUpload"
              className="relative rounded-md cursor-pointer focus-within:!outline-none focus-within:!ring-0 focus-within:!ring-offset-0"
            >
              <span className="text-lg font-medium">첨부파일 업로드</span>
              <input id="fileUpload" name="fileUpload" type="file" className="sr-only" multiple={multiple} onChange={onChangeFiles} />
            </label>
            <p className="pt-1">
              첨부파일을 끌어놓거나
              <span className="px-1 font-medium text-indigo-600 cursor-pointer hover:text-indigo-400" onClick={() => document.getElementById('fileUpload')?.click()}>이 곳을 클릭</span>
              하여 파일을 첨부해주세요
            </p>
            <p className="pt-6 text-xs">
              {allowExtensions && (
              <span>{allowExtensions.join(', ')}형식</span>
              )}
              <span>, 최대 {maxSize}</span>
            </p>
          </div>
        </div>
      </div>
      <div>
        <FileList items={items} existingItems={localExistingItems} deleteHandler={handleDeleteFile} existingFileDeleteHandler={handleDeleteExistingFile} downloadHandler={downloadHandler} className="my-4" />
      </div>
    </div>
  )
}

export default Component;